import { useState, useCallback } from 'react';

export interface Notification {
    id: string;
    type: 'alert' | 'info' | 'success' | 'warning';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
    priority: 'low' | 'medium' | 'high';
}

// Sample notifications for demonstration
const sampleNotifications: Notification[] = [
    {
        id: '1',
        type: 'alert',
        title: 'Motion Detected',
        message: 'Unusual activity detected in Camera 3 - Main Entrance',
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
        read: false,
        priority: 'high'
    },
    {
        id: '2',
        type: 'warning',
        title: 'Camera Offline',
        message: 'Camera 2 - Parking Lot has lost connection',
        timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
        read: false,
        priority: 'medium'
    },
    {
        id: '3',
        type: 'success',
        title: 'System Update',
        message: 'Fall detection model updated successfully',
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
        read: true,
        priority: 'low'
    },
    {
        id: '4',
        type: 'info',
        title: 'Recording Started',
        message: 'Scheduled recording for Camera 1 - Office has started',
        timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
        read: true,
        priority: 'low'
    },
    {
        id: '5',
        type: 'alert',
        title: 'Fire Detection Alert',
        message: 'Smoke detected in Camera 4 - Storage Room',
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago
        read: false,
        priority: 'high'
    }
];

export function useNotifications() {
    const [notifications, setNotifications] = useState<Notification[]>(sampleNotifications);

    const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
        const newNotification: Notification = {
            ...notification,
            id: Date.now().toString(),
            timestamp: new Date().toISOString()
        };

        setNotifications(prev => [newNotification, ...prev]);
        
        // Handle edge device notifications
        if (notification.title.includes('Edge Device') || notification.message.includes('edge')) {
            // Emit edge device notification event
            document.dispatchEvent(new CustomEvent('edgeDeviceNotification', {
                detail: { 
                    message: notification.message, 
                    type: notification.type === 'alert' ? 'error' : notification.type 
                }
            }));
        }
    }, []);

    const markAsRead = useCallback((id: string) => {
        setNotifications(prev =>
            prev.map(notification =>
                notification.id === id
                    ? { ...notification, read: true }
                    : notification
            )
        );
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev =>
            prev.map(notification => ({ ...notification, read: true }))
        );
    }, []);

    const deleteNotification = useCallback((id: string) => {
        setNotifications(prev =>
            prev.filter(notification => notification.id !== id)
        );
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    return {
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification
    };
}
